import { forwardRef, useImperativeHandle, useRef, type RefObject } from 'react';

import { createBaseInput } from '@/core/tamagoshiTailwind/components/Input/createBaseInput';
import { BaseInputIcon } from '@/core/tamagoshiTailwind/components/Input/utils/BaseInputComponents';
import {
  type BaseInputSettings,
  type ExtractBaseInputPropsType,
} from '@/core/tamagoshiTailwind/components/Input/utils/BaseInputTypes';
import { CrossIcon } from '@/core/tamagoshiTailwind/icons/CrossIcon';
import { SearchIcon } from '@/core/tamagoshiTailwind/icons/SearchIcon';

const settings = {
  as: 'input',
  detectEmpty: false,
  rounded: 'full',
  helperText: false,
  statuses: ['default', 'disabled'],
} as const satisfies BaseInputSettings<'input'>;

const BaseInput = createBaseInput(settings);

type InputPasswordProps = ExtractBaseInputPropsType<typeof settings>;

function useUpdateCurrentValue(ref: RefObject<HTMLInputElement>) {
  const updateCurrentValue = (value: string) => {
    if (ref.current) {
      const setValue = Object.getOwnPropertyDescriptor(
        // @ts-expect-error using private API
        ref.current.__proto__,
        'value',
      )?.set;

      const inputEvent = new Event('input', { bubbles: true });
      setValue?.call(ref.current, value);

      ref.current?.dispatchEvent(inputEvent);
    }
  };

  return updateCurrentValue;
}

/**
 * A search input component.
 *
 * See the related [Figma][figma] for more details.
 *
 * [figma]: https://www.figma.com/design/KAiRlBccr6bFYmKby9wQrR?node-id=19784-19936
 */
export const InputSearch = forwardRef<HTMLInputElement, InputPasswordProps>(
  (props, ref) => {
    const inputRef = useRef<HTMLInputElement>(null);
    useImperativeHandle(ref, () => inputRef.current!);
    const updateCurrentValue = useUpdateCurrentValue(inputRef);

    const onReset = () => updateCurrentValue('');

    return (
      <BaseInput
        {...props}
        ref={inputRef}
        type="search"
        className="remove-search-decoration font-semibold outline-offset-0"
        startIcon={
          <BaseInputIcon icon={SearchIcon} color="text-foundation-primary" />
        }
        endIcon={
          <BaseInputIcon
            as="button"
            className="disabled:cursor-not-allowed
              peer-placeholder-shown:hidden"
            disabled={props.status === 'disabled'}
            icon={CrossIcon}
            onClick={onReset}
            type="button"
          />
        }
        enterKeyHint="search"
      />
    );
  },
);

InputSearch.displayName = 'InputSearch';
